import { ReactNode, forwardRef } from "react";
import { Trans } from "@lingui/macro";
import styles from "./Header.module.scss";
import classNames from "classnames";
import { Done } from "src/icons/Done";
import { InfoItalic } from "src/icons/InfoItalic";
import { locale } from "src/config/app-config";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { Text } from "@otrium/atoms";
import { Box, Flex } from "@otrium/core";
import { TrustpilotCustom } from "src/molecules/TrustpilotBox";
import { LanguageSwitcher } from "src/molecules/LanguageSwitcher";
import { AccessibilityProps } from "src/types/AccessibilityProps";
import { useMiscellaneous } from "src/hooks/useMiscellaneous";
import { useTheme, Theme } from "@emotion/react";
import { Skeleton } from "src/atoms/Skeleton";
import NoSSR from "src/atoms/NoSSR";

// Placeholder widths shown while the advantages load — mirrors the ~3 header
// items so the row keeps its shape and the text swaps in without a shift.
const SERVICE_BANNER_SKELETON_WIDTHS = ["120px", "96px", "140px"];

interface Props extends AccessibilityProps {
  children?: ReactNode;
}

const ServiceBanner = forwardRef<HTMLDivElement, Props>(function ServiceBanner(
  props,
  ref
) {
  const { ...a11yProps } = props;
  const helpCenterHref = getI18nRoute({ route: "/faq", locale });
  const { data: miscellaneousData, loading } = useMiscellaneous();
  const theme: Theme = useTheme();

  const headerOffers = miscellaneousData?.miscellaneous?.header ?? [];

  return (
    <Box backgroundColor={theme.colors.tone.black} {...a11yProps}>
      <div className={styles.container} ref={ref}>
        <div className={styles.serviceBannerContainer}>
          <NoSSR>
            <Flex
              mr="auto"
              display={["none", "none", "flex"]}
              as="ul"
              aria-label="Our advantages"
            >
              {loading
                ? SERVICE_BANNER_SKELETON_WIDTHS.map((skeletonWidth, index) => (
                    <li
                      className={classNames(
                        styles.serviceBannerOffer,
                        styles.hiddenUntilMd,
                        styles.offerSpacing
                      )}
                      key={index}
                    >
                      <Done width="14" height="14" role="presentation" />
                      <Box ml={2}>
                        <Skeleton invert width={skeletonWidth} height="16px" />
                      </Box>
                    </li>
                  ))
                : headerOffers.map((header, index) => (
                    <li
                      className={classNames(
                        styles.serviceBannerOffer,
                        styles.hiddenUntilMd,
                        styles.offerSpacing
                      )}
                      key={index}
                    >
                      <Done width="14" height="14" role="presentation" />
                      <Text ml={2}>{header}</Text>
                    </li>
                  ))}
            </Flex>
            <Box mr={[4, 4, 4, 0]}>
              <TrustpilotCustom locale={locale} />
            </Box>
            <Box display={["none", "none", "none", "flex"]} ml={3} mr={3}>
              <a className={styles.serviceBannerOffer} href={helpCenterHref}>
                <InfoItalic width={24} height={24} aria-hidden />
                <Text ml={2} mr={2} sx={{ textDecoration: "underline" }}>
                  <Trans>Help center</Trans>
                </Text>
              </a>
            </Box>
            <LanguageSwitcher
              svgPrefixId="top-header-language-switcher"
              locale={locale}
              showRegion={true}
              showLanguage={false}
              showCurrency={false}
            />
          </NoSSR>
        </div>
      </div>
    </Box>
  );
});

export { ServiceBanner };
